import { NavLink } from 'react-router-dom';
import { ChevronDown, ChevronRight, Folder, FolderOpen, MoreHorizontal, Plus, UserPlus } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { getWorkspaceIcon, getProjectIcon } from './constants';

const FolderItem = ({
  folder,
  isExpanded,
  onToggle,
  expandedWorkspaces,
  workspaceProjects,
  loadingProjects,
  currentWorkspace,
  activeProjectId,
  onToggleWorkspace,
  onSwitchWorkspace,
  onEditFolder,
  onCreateProject,
  onMoveToFolder,
  onInvite,
  onOpenSettings,
  onOpenProjectSettings,
  workspaceUnread = {},
  projectUnread = {}
}) => {
  // Calculate total unread count for the folder
  const folderUnreadCount = (folder.workspaces || []).reduce((total, ws) => {
    return total + (workspaceUnread[ws.workspace_id] || 0);
  }, 0);

  // Render workspace item inline to avoid circular imports
  const renderWorkspaceItem = (ws) => {
    const wsUnreadCount = workspaceUnread[ws.workspace_id] || 0;
    
    return (
    <Collapsible
      key={ws.workspace_id}
      open={expandedWorkspaces[ws.workspace_id]}
      onOpenChange={() => onToggleWorkspace(ws.workspace_id)}
    >
      <div className={`rounded-lg ${currentWorkspace?.workspace_id === ws.workspace_id ? 'bg-[#4A9CC7]/5' : ''} ${wsUnreadCount > 0 ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
        <CollapsibleTrigger asChild>
          <div
            className="flex items-center gap-2 py-1.5 px-3 rounded-lg cursor-pointer hover:bg-muted group"
            data-testid={`workspace-${ws.workspace_id}`}
          >
            <div className="flex items-center justify-center w-5 h-5">
              {expandedWorkspaces[ws.workspace_id] ? (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              )}
            </div>
            <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
              ws.workspace_type === 'team' ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400' :
              ws.workspace_type === 'client' ? 'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400' :
              'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-400'
            }`}>
              {getWorkspaceIcon(ws.workspace_type)}
            </div>
            <span 
              className={`flex-1 truncate text-xs font-medium text-foreground min-w-[60px] ${wsUnreadCount > 0 ? 'font-semibold' : ''}`}
              title={ws.name}
              onClick={(e) => {
                e.stopPropagation();
                onSwitchWorkspace(ws);
              }}
            >
              {ws.name}
            </span>
            
            {/* Unread badge */}
            {wsUnreadCount > 0 && (
              <Badge 
                className="bg-[#4A9CC7] text-white text-[10px] px-1.5 py-0 min-w-[18px] h-4 flex items-center justify-center"
                data-testid={`workspace-unread-${ws.workspace_id}`}
              >
                {wsUnreadCount > 99 ? '99+' : wsUnreadCount}
              </Badge>
            )}
            
            <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5">
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0 text-muted-foreground hover:text-[#4A9CC7]"
                onClick={(e) => onCreateProject(ws, e)}
                title="Add Project"
              >
                <Plus className="w-3 h-3" />
              </Button>
              {(ws.user_role === 'owner' || ws.user_role === 'admin') && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0 text-muted-foreground hover:text-[#4A9CC7]"
                  onClick={(e) => onMoveToFolder(ws, e)}
                  title="Move to Folder"
                >
                  <Folder className="w-3 h-3" />
                </Button>
              )}
              {ws.workspace_type !== 'personal' && (ws.user_role === 'owner' || ws.user_role === 'admin') && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0 text-muted-foreground hover:text-[#4A9CC7]"
                    onClick={(e) => onInvite(ws, e)}
                    title="Invite"
                  >
                    <UserPlus className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0 text-muted-foreground hover:text-[#4A9CC7]"
                    onClick={(e) => onOpenSettings(ws, e)}
                    title="Settings"
                  >
                    <MoreHorizontal className="w-3 h-3" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <div className="ml-8 pl-2 border-l border-border space-y-0.5 py-1">
            {loadingProjects[ws.workspace_id] ? (
              <div className="text-xs text-muted-foreground py-1 px-2">Loading...</div>
            ) : (workspaceProjects[ws.workspace_id] || []).length === 0 ? (
              <button
                onClick={() => onCreateProject(ws)}
                className="flex items-center gap-2 px-2 py-1 text-xs text-muted-foreground hover:text-[#4A9CC7] w-full"
              >
                <Plus className="w-3 h-3" />
                Add first project
              </button>
            ) : (
              <>
                {(workspaceProjects[ws.workspace_id] || []).map(proj => {
                  const isActive = activeProjectId === proj.project_id;
                  const projUnreadCount = projectUnread[proj.project_id] || 0;
                  return (
                    <div
                      key={proj.project_id}
                      className={`flex items-center gap-2 px-2 py-1 rounded text-xs transition-all group/proj ${
                        isActive ? 'font-medium shadow-sm' : 'text-muted-foreground hover:bg-muted'
                      } ${projUnreadCount > 0 ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                      style={isActive ? {
                        backgroundColor: `${proj.color || '#4A9CC7'}20`,
                        color: proj.color || '#4A9CC7',
                        borderLeft: `3px solid ${proj.color || '#4A9CC7'}`
                      } : {}}
                      data-testid={`project-${proj.project_id}`}
                    >
                      <NavLink
                        to={`/tasks?workspace=${ws.workspace_id}&project=${proj.project_id}`}
                        className="flex items-center gap-2 flex-1 min-w-0"
                        title={proj.name}
                      >
                        <div 
                          className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0" 
                          style={{ 
                            backgroundColor: `${proj.color || '#4A9CC7'}20`,
                            color: proj.color || '#4A9CC7'
                          }}
                        >
                          {getProjectIcon(proj.icon)}
                        </div>
                        <span className={`truncate ${projUnreadCount > 0 ? 'font-semibold text-foreground' : ''}`}>{proj.name}</span>
                      </NavLink>
                      
                      {/* Project unread badge */}
                      {projUnreadCount > 0 && (
                        <Badge 
                          className="bg-[#4A9CC7] text-white text-[10px] px-1 py-0 min-w-[16px] h-4 flex items-center justify-center"
                          data-testid={`project-unread-${proj.project_id}`}
                        >
                          {projUnreadCount > 99 ? '99+' : projUnreadCount}
                        </Badge>
                      )}
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0 opacity-0 group-hover/proj:opacity-100 text-muted-foreground hover:text-[#4A9CC7] flex-shrink-0"
                        onClick={(e) => onOpenProjectSettings(proj, ws, e)}
                        title="Project Settings"
                      >
                        <MoreHorizontal className="w-3 h-3" />
                      </Button>
                    </div>
                  );
                })}
                <button
                  onClick={() => onCreateProject(ws)}
                  className="flex items-center gap-2 px-2 py-1 text-xs text-muted-foreground hover:text-[#4A9CC7] w-full"
                >
                  <Plus className="w-3 h-3" />
                  Add project
                </button>
              </>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )};

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <div className={`rounded-lg ${folderUnreadCount > 0 ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
        <CollapsibleTrigger asChild>
          <div
            className="flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-muted group"
            data-testid={`folder-${folder.folder_id}`}
          >
            <div className="flex items-center justify-center w-5 h-5">
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              )}
            </div>
            <div 
              className="w-6 h-6 rounded flex items-center justify-center"
              style={{ 
                backgroundColor: `${folder.color || '#6B7280'}20`,
                color: folder.color || '#6B7280'
              }}
            >
              {isExpanded ? (
                <FolderOpen className="w-4 h-4" />
              ) : (
                <Folder className="w-4 h-4" />
              )}
            </div>
            <span className={`flex-1 truncate text-xs font-medium text-foreground min-w-[60px] ${folderUnreadCount > 0 ? 'font-semibold' : ''}`} title={folder.name}>
              {folder.name}
            </span>
            
            {/* Folder unread badge */}
            {folderUnreadCount > 0 && (
              <Badge 
                className="bg-[#4A9CC7] text-white text-[10px] px-1.5 py-0 min-w-[18px] h-4 flex items-center justify-center"
                data-testid={`folder-unread-${folder.folder_id}`}
              >
                {folderUnreadCount > 99 ? '99+' : folderUnreadCount}
              </Badge>
            )}
            
            <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5">
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0 text-muted-foreground hover:text-[#4A9CC7]"
                onClick={(e) => onEditFolder(folder, e)}
                title="Edit Folder"
              >
                <MoreHorizontal className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <div className="ml-4 border-l-2 border-border space-y-0.5 py-1" style={{ borderColor: `${folder.color || '#6B7280'}40` }}>
            {(folder.workspaces || []).length === 0 ? (
              <div className="text-xs text-muted-foreground py-1 px-3 italic">No workspaces</div>
            ) : (
              folder.workspaces.map(ws => renderWorkspaceItem(ws))
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export default FolderItem;
